import simplify from '@turf/simplify'
import pubsub from '@/lib/pubsub'
import DrawModeBase from '@/lib/mapbox/draw-mode-base'
import { getToleranceDegrees } from '@/lib/mapbox/draw-mode-helpers'
import { setCursor, resetCursor } from '@/services/map-service'
import { MARKUP_COORDINATES_CHANGED } from '@/lib/mapbox/drawing-events'

/**
 * Allows the user to draw a new line by clicking and free-hand drawing the
 * vertices.
 */
class FreehandDrawLineMode extends DrawModeBase {
  /**
   * @inheritDoc
   */
  onSetup(lineGeoJson) {
    const lineId = lineGeoJson.properties.id
    lineGeoJson.geometry.coordinates = []
    setCursor('crosshair')

    this._state = {
      lineId,
      isDrawing: false,
      cursorLocation: null,
      spacebarPressed: false,
      handleUndo: this._handleUndo.bind(this),
      handleSpacebar: this._handleSpacebar.bind(this),
      undoQueue: [],
      freeDrawStartVertexIndex: null,
    }

    pubsub.subscribe('markupUndo', this._state.handleUndo)

    document.addEventListener('keydown', this._state.handleSpacebar)
    document.addEventListener('keyup', this._state.handleSpacebar)
  }

  /**
   * Handle the keydown and keyup events for pressing the spacebar
   * @param {Object} evt - listener event
   */
  _handleSpacebar(evt) {
    if (evt.keyCode === 32) {
      if (evt.type === 'keydown') {
        this._state.spacebarPressed = true
      } else if (evt.type === 'keyup') {
        this._state.spacebarPressed = false
      }
    }
  }

  /**
   * @inheritdoc
   */
  onTearDown() {
    resetCursor()
    if (this._state.handleUndo) {
      pubsub.unsubscribe('markupUndo', this._state.handleUndo)
    }
    document.removeEventListener('keydown', this._state.handleSpacebar)
    document.removeEventListener('keyup', this._state.handleSpacebar)
  }

  /**
   * @inheritDoc
   */
  onMouseDown(event) {
    // allow user to pan the map when the spacebar is pressed
    if (this._state.spacebarPressed) {
      return
    }

    this._state.isDrawing = true
    this._state.cursorLocation = [event.lngLat.lng, event.lngLat.lat]
    this._state.freeDrawStartVertexIndex = this._getCoordinates()?.length
    this.map.dragPan.disable()
    this.updated()
  }

  /**
   * @inheritDoc
   */
  onMouseUp() {
    // if spacebar was pressed, user was panning the map
    if (this._state.spacebarPressed) {
      return
    }

    this._state.isDrawing = false
    this.map.dragPan.enable()
    const lineId = this._state.lineId
    const feature = this.getFeature(lineId)
    const tolerance = getToleranceDegrees(2)
    const coords = feature.geometry.coordinates
    // make sure there is at least 1 coord and that the first and last coord are not the same
    if (
      tolerance &&
      coords.length > 1 &&
      coords[0] !== coords[coords.length - 1]
    ) {
      simplify(feature.geometry, { tolerance, mutate: true })
    }
    this.events.emit(MARKUP_COORDINATES_CHANGED, {
      id: lineId,
      coordinates: feature.geometry.coordinates,
    })
    this._state.undoQueue.push({
      type: 'free_draw',
      index: this._state.freeDrawStartVertexIndex,
    })
  }

  /**
   * @inheritDoc
   */
  onMouseMove(event) {
    if (this._state.isDrawing) {
      this._state.cursorLocation = [event.lngLat.lng, event.lngLat.lat]
      this.updated()
    }
  }

  /**
   * @inheritDoc
   */
  onMouseOut() {
    // If the user is drawing when the mouse leaves the map, treat that the same as a mouseUp event
    if (this._state.isDrawing) {
      this.onMouseUp()
    }
  }

  /**
   * @inheritDoc
   */
  toDisplayFeatures(geoJson) {
    const line = this._formatLine(geoJson)

    return [line]
  }

  /**
   * Formats a line for display.
   * @private
   * @param {Object} lineGeoJson
   * @returns {Array<Object>}
   */
  _formatLine(lineGeoJson) {
    const { cursorLocation, isDrawing } = this._state

    if (isDrawing && cursorLocation !== null) {
      lineGeoJson.geometry.coordinates.push(cursorLocation)
      this._state.vertexAddLocation = null
    }

    return lineGeoJson
  }

  /**
   * Handles the undo button clicked.
   */
  _handleUndo() {
    const command = this._state.undoQueue.pop()
    if (!command) {
      return
    }

    this._undoFreeDraw(command)
  }

  /**
   * Undo free draw command
   * @param {object} command - Free draw command
   */
  _undoFreeDraw(command) {
    const coordinates = this._getCoordinates()
    coordinates.splice(command.index)
    this.updated()

    this.events.emit(MARKUP_COORDINATES_CHANGED, {
      id: this._state.lineId,
      coordinates: coordinates,
    })
  }

  /**
   * Gets the line coordinates
   * @return {array} coordinates
   */
  _getCoordinates() {
    const lineFeature = this._getFeature()
    return lineFeature.geometry.coordinates
  }

  /**
   * Returns the line feature
   * @return {object} Line feature
   */
  _getFeature() {
    const lineId = this._state.lineId
    return this.getFeature(lineId)
  }
}

export default FreehandDrawLineMode
